import React, { useState, useEffect } from 'react';
import './PortionMultiplierSettings.css';
import UnitMappingService from '../services/unitMappingService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

const PortionMultiplierSettings = ({ onClose }) => {
  const [multipliers, setMultipliers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [newMultiplier, setNewMultiplier] = useState('1.0');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingKeyword, setEditingKeyword] = useState(null);

  // Default multipliers from itemmap.js
  const defaultMultipliers = {
    // INDIVIDUAL SERVINGS
    'mini': 0.5,
    'quarter': 0.25,
    'solo': 0.75,
    'personal': 1.0,
    // STANDARD SERVINGS
    'single': 1.0,
    'regular': 1.0,
    'medium': 1.0,
    'full': 1.0,
    'half': 0.5,
    // SMALL SIZES
    'small': 0.6,
    'starter': 0.4,
    // LARGE SERVINGS
    'large': 1.5,
    'jumbo': 2.0,
    'king': 2.0,
    'xl': 2.0,
    // FAMILY/SHARING SIZES
    'double': 2.0,
    'family': 2.5,
    'sharing': 2.5,
    'bonanza': 3.0,
    'mega': 3.5
  };

  useEffect(() => {
    loadMultipliers();
  }, []);

  const loadMultipliers = async () => {
    setLoading(true);
    setError('');
    try {
      const businessId = UnitMappingService.getBusinessId();
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }
      
      if (!businessId) {
        throw new Error('No business ID found. Please log in again.');
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/portion-multipliers`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-business-id': businessId
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `Server error: ${response.status}`);
      }
      
      if (data.success) {
        // Merge defaults with custom multipliers (custom overrides defaults)
        const mergedMultipliers = { ...defaultMultipliers, ...data.multipliers };
        setMultipliers(mergedMultipliers);
      } else {
        throw new Error(data.error || 'Failed to load multipliers');
      }
    } catch (err) {
      console.error('Error loading multipliers:', err);
      setError(`Failed to load multipliers. Using defaults. ${err.message}`);
      setMultipliers(defaultMultipliers);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const businessId = UnitMappingService.getBusinessId();
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }
      
      if (!businessId) {
        throw new Error('No business ID found. Please log in again.');
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/portion-multipliers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-business-id': businessId
        },
        body: JSON.stringify({ multipliers })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `Server error: ${response.status}`);
      }
      
      if (data.success) {
        setSuccess(`✅ Saved ${data.count} multiplier(s) successfully!`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error(data.error || 'Failed to save multipliers');
      }
    } catch (err) {
      console.error('Error saving multipliers:', err);
      setError('❌ Failed to save multipliers: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddNew = () => {
    const keyword = newKeyword.toLowerCase().trim();
    
    if (!keyword) {
      setError('Please enter a keyword');
      return;
    }
    
    if (!/^[a-z0-9-]+$/.test(keyword)) {
      setError('Keyword can only contain lowercase letters, numbers, and hyphens');
      return;
    }
    
    const multiplierValue = parseFloat(newMultiplier);
    if (isNaN(multiplierValue) || multiplierValue < 1 || multiplierValue > 10) {
      setError('Multiplier must be between 1 and 10.0');
      return;
    }
    
    if (multipliers[keyword] !== undefined) {
      setError('This keyword already exists. Edit it instead.');
      return;
    }
    
    setMultipliers(prev => ({
      ...prev,
      [keyword]: multiplierValue
    }));
    
    setNewKeyword('');
    setNewMultiplier('1.0');
    setError('');
    setSuccess(`✅ Added "${keyword}" with multiplier ${multiplierValue}x. Click "Save All Changes" to persist.`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleEdit = (keyword, newValue) => {
    // Allow empty string for user to delete and re-enter
    if (newValue === '') {
      setMultipliers(prev => ({
        ...prev,
        [keyword]: ''
      }));
      return;
    }
    
    const multiplierValue = parseFloat(newValue);
    if (isNaN(multiplierValue) || multiplierValue < 0 || multiplierValue > 10) {
      return;
    }
    
    // Round to 2 decimal places
    const roundedValue = Math.round(multiplierValue * 100) / 100;
    
    setMultipliers(prev => ({
      ...prev,
      [keyword]: roundedValue
    }));
  };

  const handleDelete = async (keyword) => {
    if (!window.confirm(`Are you sure you want to delete the multiplier for "${keyword}"? This will revert to the default value.`)) {
      return;
    }
    
    try {
      const businessId = UnitMappingService.getBusinessId();
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }
      
      if (!businessId) {
        throw new Error('No business ID found. Please log in again.');
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/portion-multipliers/${keyword}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-business-id': businessId
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `Server error: ${response.status}`);
      }
      
      if (data.success) {
        // If it's a default keyword, reset to default value
        if (defaultMultipliers[keyword] !== undefined) {
          setMultipliers(prev => ({
            ...prev,
            [keyword]: defaultMultipliers[keyword]
          }));
          setSuccess(`✅ Reset "${keyword}" to default value (${defaultMultipliers[keyword]}x)`);
        } else {
          // If it's a custom keyword, remove it completely
          setMultipliers(prev => {
            const newMultipliers = { ...prev };
            delete newMultipliers[keyword];
            return newMultipliers;
          });
          setSuccess(`✅ Deleted custom keyword "${keyword}"`);
        }
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error(data.error || 'Failed to delete multiplier');
      }
    } catch (err) {
      console.error('Error deleting multiplier:', err);
      setError('❌ Failed to delete multiplier: ' + err.message);
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm('Are you sure you want to reset ALL multipliers to default values? This will delete all your custom settings.')) {
      return;
    }
    
    try {
      const businessId = UnitMappingService.getBusinessId();
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }
      
      if (!businessId) {
        throw new Error('No business ID found. Please log in again.');
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/portion-multipliers`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-business-id': businessId
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `Server error: ${response.status}`);
      }
      
      if (data.success) {
        setMultipliers(defaultMultipliers);
        setSuccess(`✅ Reset all multipliers to defaults (deleted ${data.deletedCount} custom setting(s))`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error(data.error || 'Failed to reset multipliers');
      }
    } catch (err) {
      console.error('Error resetting multipliers:', err);
      setError('❌ Failed to reset multipliers: ' + err.message);
    }
  };

  const isCustomKeyword = (keyword) => {
    return defaultMultipliers[keyword] === undefined;
  };

  const isModified = (keyword) => {
    return defaultMultipliers[keyword] !== undefined && 
           defaultMultipliers[keyword] !== multipliers[keyword];
  };

  const sortedKeywords = Object.keys(multipliers).sort();

  if (loading) {
    return (
      <div className="multiplier-settings">
        <div className="settings-header">
          <div className="header-left">
            <button onClick={onClose} className="back-btn" aria-label="Back">
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <h2>Portion Multiplier Settings</h2>
          </div>
          <button onClick={onClose} className="close-btn">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading multipliers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="multiplier-settings">
      <div className="settings-header">
        <div className="header-left">
          <button onClick={onClose} className="back-btn" aria-label="Back">
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <h2>Portion Multiplier Settings</h2>
        </div>
        <button onClick={onClose} className="close-btn">
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      <div className="add-new-section">
        <h3>Add New Multiplier</h3>
        <div className="add-new-form">
          <input
            type="text"
            placeholder="Keyword (e.g., super-jumbo)"
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            className="keyword-input"
          />
          <input
            type="number"
            step="0.1"
            min="1"
            max="10"
            placeholder="Multiplier"
            value={newMultiplier}
            onChange={(e) => setNewMultiplier(e.target.value)}
            className="multiplier-input"
          />
          <button onClick={handleAddNew} className="btn btn-primary">
            Add
          </button>
        </div>
        <p className="hint">
          Use lowercase letters, numbers, and hyphens only. Multiplier range: 1 to 10.0
        </p>
      </div>

      <div className="multipliers-table-container">
        <div className="table-actions">
          <h3>Current Multipliers ({sortedKeywords.length})</h3>
          <div className="action-buttons">
            <button onClick={handleResetAll} className="btn btn-danger">
              Reset All to Defaults
            </button>
            <button onClick={handleSaveAll} disabled={saving} className="btn btn-success">
              {saving ? 'Saving...' : 'Save All Changes'}
            </button>
          </div>
        </div>

        <table className="multipliers-table">
          <thead>
            <tr>
              <th>Keyword</th>
              <th>Multiplier</th>
            </tr>
          </thead>
          <tbody>
            {sortedKeywords.map(keyword => (
              <tr key={keyword} className={isCustomKeyword(keyword) ? 'custom-row' : ''}>
                <td>
                  <span className="keyword-label">{keyword}</span>
                  {isCustomKeyword(keyword) && (
                    <span className="badge badge-custom">CUSTOM</span>
                  )}
                  {isModified(keyword) && (
                    <span className="badge badge-modified">MODIFIED</span>
                  )}
                </td>
                <td>
                  {editingKeyword === keyword ? (
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="10"
                      value={multipliers[keyword]}
                      onChange={(e) => handleEdit(keyword, e.target.value)}
                      onBlur={() => setEditingKeyword(null)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          setEditingKeyword(null);
                        }
                      }}
                      onFocus={(e) => e.target.select()}
                      className="edit-multiplier-input"
                      autoFocus
                    />
                  ) : (
                    <span 
                      className="multiplier-value" 
                      onClick={() => setEditingKeyword(keyword)}
                    >
                      {multipliers[keyword].toFixed(2)}×
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PortionMultiplierSettings;
